import { ProtectedRoute } from "@/lib/auth/protected-route"
import { AppLayout } from "@/components/layout/app-layout"
import { useAuth } from "@/lib/auth/auth-context"
import { useOrganization } from "@/lib/organization/organization-context"
import { useEstimates, useGroupEstimates } from "@/lib/api/use-estimates"
import { Roles, ALL_ROLES } from "@/lib/team/roles"
import { useCurrentRole } from "@/lib/team/use-current-role"
import { SADashboardView } from "@/components/dashboard/sa-dashboard-view"
import { OpportunityRoom } from "@/components/dashboard/room/opportunity-room"
import type { ViewMode } from "@/components/dashboard/room/room-model"

const greet = () => {
  const h = new Date().getHours()
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening"
}

export default function DashboardPage() {
  const { user } = useAuth()
  const { activeTeamId, activeTeamKind, isGroupView, groupDealerships, activeGroup, activeDealership } = useOrganization()

  const singleQuery = useEstimates(!isGroupView ? activeTeamId : null)
  const groupQuery = useGroupEstimates(groupDealerships, isGroupView)
  const estimates = (isGroupView ? groupQuery.data : singleQuery.data) ?? []
  const isLoadingEstimates = isGroupView ? groupQuery.isLoading : singleQuery.isLoading

  const currentRole = useCurrentRole()
  // Service Advisors keep the wallet-first adoption view; every other role
  // (and super-admins) gets the opportunity dashboard.
  const isServiceAdvisor = currentRole === Roles.ServiceAdvisor && !user?.admin

  // Don't split `user.name` — `name` is `firstName + " " + lastName`, so a
  // multi-word firstName ("Body Shop") gets truncated to its first token
  // ("Good morning, Body"). Use the raw firstName field instead.
  const firstName = user?.firstName?.trim() || "there"
  const role = user?.admin ? "Admin" : currentRole ?? ""
  const today = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })

  const teamLabel = isGroupView && activeGroup
    ? `${activeGroup.name} · ${groupDealerships.length} dealerships`
    : activeDealership?.name ?? null

  const canQuick =
    !!user?.admin ||
    currentRole === Roles.ServiceAdvisor ||
    currentRole === Roles.Estimator ||
    currentRole === Roles.ServiceManager ||
    currentRole === Roles.GeneralManager

  // Role-tailored emphasis (see roomConfig): the body shop gets the simple
  // opportunity view, a dealership service manager gets the adoption view,
  // everyone else (Director/GM/admin) gets the full room.
  const viewMode: ViewMode = !isGroupView && activeTeamKind === "bodyshop"
    ? "bodyshop"
    : currentRole === Roles.ServiceManager && !user?.admin
      ? "service"
      : "full"

  return (
    <ProtectedRoute allowedRoles={ALL_ROLES}>
      <AppLayout>
        {isServiceAdvisor ? (
          <SADashboardView
            user={user}
            estimates={estimates as any}
            firstName={firstName}
            greet={greet()}
            today={today}
            canQuick={canQuick}
          />
        ) : (
          <OpportunityRoom
            estimates={estimates}
            isLoading={isLoadingEstimates}
            isGroupView={isGroupView}
            teamId={activeTeamId}
            viewMode={viewMode}
            firstName={firstName}
            greet={greet()}
            today={today}
            role={role}
            teamLabel={teamLabel}
            canQuick={canQuick}
          />
        )}
      </AppLayout>
    </ProtectedRoute>
  )
}
